import React, { useEffect, useMemo, useState } from "react";
import { Input, Button, Modal, Select, Alert, message } from "antd";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { useForm, Controller } from "react-hook-form";
import axios from "axios";
import ButtonLoader from "../ButtonLoader";
import ImageCropUpload from "../Common/ImageCropUpload";
import BrandForm from "../Brands/BrandForm";

const schema = yup.object().shape({
  brand_id: yup.number().required("Brand is required"),
  model: yup.string().required("Model is required"),
  location_id: yup.number().required("Facility is required"),
  status: yup.string().required("Status is required"),
});

const InventoryForm = ({
  isModalOpen,
  editingInventory,
  closeModal,
  handleUpdateList,
}) => {
  const [loading, setLoading] = useState(true);
  const [showErrors, setShowErrors] = useState(false);
  const [errorsMessage, setErrorsMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isDataSet, setIsDataSet] = useState(false);

  const [brandOptions, setBrandOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [facilities, setFacilities] = useState({ type: "flat", locations: [] });
  const [isBrandModalOpen, setIsBrandModalOpen] = useState(false);
  const [serialNumberImage, setSerialNumberImage] = useState(null);
  const [tvPhoto, setTvPhoto] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    context: { isEdit: !!editingInventory },
    defaultValues: { status: "in_use" },
  });

  // Buildings with floors: only the floors are selectable options, per the
  // grouped facility fallback. A standalone building with no floors of its
  // own is selectable directly, since there's nothing else to pick.
  const groupedFacilityOptions = useMemo(() => {
    if (facilities.type !== "grouped") return null;
    const all = facilities.locations || [];
    const buildings = all.filter((loc) => !loc.parent_id);
    return buildings.map((building) => {
      const floors = all.filter((loc) => loc.parent_id === building.id);
      return { building, floors };
    });
  }, [facilities]);

  const fetchMasterData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/inventory/masters`
      );
      setBrandOptions(response.data.data.brands);
      setStatusOptions(response.data.data.status);
      setFacilities(response.data.data.facilities);
      setLoading(false);
    } catch (err) {
      console.error(err.message);
      setLoading(false);
    }
  };

  const fetchInventory = async (id) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/inventory/${id}`
      );
      const editFormData = { ...response.data.data };
      if (editFormData.serial_number_image) {
        setSerialNumberImage({ file_path: editFormData.serial_number_image });
      }
      if (editFormData.tv_photo) {
        setTvPhoto({ file_path: editFormData.tv_photo });
      }
      reset(editFormData);
      setLoading(false);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    const fetchDataAndSetState = async () => {
      await fetchMasterData();
      setIsDataSet(true);
    };
    fetchDataAndSetState();
  }, []);

  useEffect(() => {
    if (isDataSet && editingInventory) {
      fetchInventory(editingInventory.id);
    }
  }, [isDataSet, editingInventory]);

  const handleBrandCreated = async (insertedId) => {
    await fetchMasterData();
    if (insertedId) {
      setValue("brand_id", insertedId, { shouldValidate: true, shouldDirty: true });
    }
    setIsBrandModalOpen(false);
  };

  const onSubmitInventory = async (data) => {
    let differFormFields = {};
    if (editingInventory !== null) {
      differFormFields = { id: editingInventory.id };
    }
    const finalFormData = {
      brand_id: data.brand_id,
      serial_number: data.serial_number ? data.serial_number.trim() : "",
      serial_number_image: serialNumberImage?.file_path || null,
      model: data.model.trim(),
      location_id: data.location_id,
      position: data.position ? data.position.trim() : "",
      tv_photo: tvPhoto?.file_path || null,
      status: data.status,
      ...differFormFields,
    };
    setSubmitting(true);
    const url =
      editingInventory === null
        ? `${process.env.REACT_APP_BASE_URL}/api/inventory/create`
        : `${process.env.REACT_APP_BASE_URL}/api/inventory/update`;

    try {
      if (editingInventory === null) {
        await axios.post(url, finalFormData);
        message.success("Inventory item created successfully");
      } else {
        const response = await axios.put(url, finalFormData);
        const updated_row_count = response.data.data.updated_row_count;
        if (updated_row_count === 1) {
          message.success("Inventory item updated successfully");
        }
      }
      setSubmitting(false);
      setShowErrors(false);
      handleUpdateList();
      closeModal();
    } catch (error) {
      if (error.response && error.response.status > 300) {
        const errorData = error.response.data.message;
        setErrorsMessage(errorData);
        setShowErrors(true);
        setTimeout(() => setShowErrors(false), 3000);
        setSubmitting(false);
      } else {
        console.error("Error:", error);
        setSubmitting(false);
      }
    }
  };

  return (
    <Modal
      title={editingInventory ? "Edit Inventory" : "Add New Inventory"}
      open={isModalOpen}
      onCancel={() => {
        reset();
        closeModal();
      }}
      footer={null}
      maskClosable={false}
      width={600}
    >
      {loading ? (
        <ButtonLoader wrapperClass={`min-h-50`} />
      ) : (
        <>
          {showErrors && (
            <div className="col-sm-12 mb-3">
              <Alert message={errorsMessage} type="error" closeIcon={false} />
            </div>
          )}
          <form onSubmit={handleSubmit(onSubmitInventory)}>
            <div className="mb-3">
              <label className="form-label">
                Brand <span className="text-danger">*</span>
              </label>
              <Controller
                name="brand_id"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    style={{ width: "100%" }}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.children ?? "").toString().toLowerCase().includes(input.toLowerCase())
                    }
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        <div className="p-2 border-top">
                          <Button
                            type="link"
                            block
                            onClick={() => setIsBrandModalOpen(true)}
                          >
                            + Add new brand
                          </Button>
                        </div>
                      </>
                    )}
                  >
                    {brandOptions.map((brand) => (
                      <Select.Option key={brand.id} value={brand.id}>
                        {brand.title}
                      </Select.Option>
                    ))}
                  </Select>
                )}
              />
              {errors.brand_id && (
                <div className="text-danger small">{errors.brand_id.message}</div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label">Serial Number</label>
              <Controller
                name="serial_number"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
            </div>

            <div className="mb-3">
              <label className="form-label d-block">Serial Number Image</label>
              <ImageCropUpload
                value={serialNumberImage}
                onChange={setSerialNumberImage}
                getSignedUrlEndpoint="/api/inventory/get-signed-url"
                label="Serial Number Image"
                aspect={4 / 3}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">
                Model <span className="text-danger">*</span>
              </label>
              <Controller
                name="model"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
              {errors.model && (
                <div className="text-danger small">{errors.model.message}</div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label">
                Facility <span className="text-danger">*</span>
              </label>
              <Controller
                name="location_id"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    style={{ width: "100%" }}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.children ?? "").toString().toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {facilities.type === "flat"
                      ? facilities.locations.map((loc) => (
                          <Select.Option key={loc.id} value={loc.id}>
                            {loc.name}
                          </Select.Option>
                        ))
                      : groupedFacilityOptions.map(({ building, floors }) =>
                          floors.length > 0 ? (
                            <Select.OptGroup key={building.id} label={building.name}>
                              {floors.map((floor) => (
                                <Select.Option key={floor.id} value={floor.id}>
                                  {floor.name}
                                </Select.Option>
                              ))}
                            </Select.OptGroup>
                          ) : (
                            <Select.Option key={building.id} value={building.id}>
                              {building.name}
                            </Select.Option>
                          )
                        )}
                  </Select>
                )}
              />
              {errors.location_id && (
                <div className="text-danger small">{errors.location_id.message}</div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label">Position</label>
              <Controller
                name="position"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
            </div>

            <div className="mb-3">
              <label className="form-label d-block">TV Photo</label>
              <ImageCropUpload
                value={tvPhoto}
                onChange={setTvPhoto}
                getSignedUrlEndpoint="/api/inventory/get-signed-url"
                label="TV Photo"
                aspect={16 / 9}
              />
            </div>

            <div className="mb-3">
              <label className="form-label me-3">
                Status <span className="text-danger">*</span>
              </label>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select {...field} style={{ width: 220 }}>
                    {statusOptions.map((status) => (
                      <Select.Option key={status.value} value={status.value}>
                        {status.label}
                      </Select.Option>
                    ))}
                  </Select>
                )}
              />
              {errors.status && (
                <div className="text-danger small">{errors.status.message}</div>
              )}
            </div>

            <Button type="primary" htmlType="submit" block disabled={submitting}>
              {!submitting ? (
                editingInventory ? "Update Inventory" : "Add Inventory"
              ) : (
                <ButtonLoader text="Submitting..." />
              )}
            </Button>
          </form>

          {isBrandModalOpen && (
            <BrandForm
              isModalOpen={isBrandModalOpen}
              editingBrand={null}
              minimal
              closeModal={() => setIsBrandModalOpen(false)}
              handleUpdateList={handleBrandCreated}
            />
          )}
        </>
      )}
    </Modal>
  );
};

export default InventoryForm;
